'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { cn } from '@/app/lib/utils';
import Input from '../ui/Input';
import CustomSelect, { Option } from '../ui/CustomSelect';
import { Button } from '../ui/Button';
import Icon from '../ui/Icon';
import DatePicker from '../ui/DatePicker';
import useFilter from '@/app/hooks/use-filter';
import { useTranslations } from 'next-intl';
import { CustomScroll } from '../ui/CustomScroll';
import Checkbox from '../ui/Checkbox';
import CompanySelect from './CompanySelect';
import UserSelect from './UserSelect';
import ContactSelect from './ContactSelect';
import CampaignSelect from './CampaignSelect';
import IcpSelect from './IcpSelect';
import PersonaSelect from './PersonaSelect';
import TagsInput from '../ui/TagsInput';
import TargetlistSelect from './TargetlistSelect';
import RangeSlider from '../ui/RangeSlider';
import WebsiteUrlRulesInput from '../signals/WebsiteUrlRulesInput';
import type { WebsiteUrlRule, WebsiteUrlRuleOperator } from '@/types/websiteUrlRule';
import Switch from '../ui/Switch';

const FILTER_INPUT_CLASS_NAME = 'h-[38px] py-2';
const FILTER_INPUT_WRAPPER_CLASS_NAME = 'h-[38px] rounded-lg shadow-xs';
const FILTER_SELECT_CLASS_NAME = 'h-[38px] rounded-lg shadow-xs';
const FILTER_LABEL_CLASS_NAME = 'text-sm font-medium mb-1.5 text-muted-foreground';

export type FilterFieldType =
  | 'text'
  | 'email'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'select'
  | 'multiselect'
  | 'search'
  | 'daterange'
  | 'checkbox'
  | 'user-select'
  | 'company-select'
  | 'contact-select'
  | 'icp-select'
  | 'campaign-select'
  | 'persona-select'
  | 'target-list-select'
  | 'tag-input'
  | 'range-slider'
  | 'range-select'
  | 'number-range'
  | 'switch'
  | 'divider'
  | 'website-url-rules';

export interface FilterField {
  name: string;
  label?: string;
  description?: string;
  type: FilterFieldType;
  placeholder?: string;
  defaultValue?: any;
  options?: Option[];
  required?: boolean;
  disabled?: boolean;
  filterParams?: Record<string, string>;
  className?: string;
  wrapperClassName?: string;
  keys?: string[];
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    message?: string;
  };
  rangeMin?: number;
  rangeMax?: number;
  rangeStep?: number;
  showRangeSlider?: boolean;
  rangeMinLabel?: string;
  rangeMaxLabel?: string;
  searchable?: boolean;
  searchLoading?: boolean;
  onSearch?: (search: string, isOnClose?: boolean) => void;
  clearable?: boolean;
  multiple?: boolean;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  hidden?: boolean;
  maxRules?: number;
  includeTargetPageMetadata?: boolean;
  isActive?: boolean;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  websiteUrlRuleLabels?: {
    enterUrl: string;
    visitsPlaceholder: string;
    visitCount: string;
    targetPages?: string;
    totalTimeSpent?: string;
    timeSpentPlaceholder?: string;
    exactMatch: string;
    matchTypes?: {
      exact: string;
      contains: string;
      starts_with: string;
      ends_with: string;
    };
    visits: string;
    time: string;
    clearTime: string;
    minuteSuffix: string;
    secondSuffix: string;
    add: string;
    remove: string;
    operators: Record<WebsiteUrlRuleOperator, string>;
  };
}

export interface FilterProps {
  fields: FilterField[];
  onSubmit?: (values: Record<string, any>, additionalData?: Record<string, any>) => void;
  onReset?: () => void;
  onFieldChange?: (fieldName: string, value: any, additionalData?: any) => void;
  className?: string;
  wrapperClassName?: string;
  showResetButton?: boolean;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  resetButtonText?: string;
  footerClassName?: string;
  layout?: 'horizontal' | 'vertical' | 'grid';
  columns?: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  title?: string;
  subtitle?: string;
}

const Filter: React.FC<FilterProps> = ({
  fields,
  onSubmit,
  onReset,
  onFieldChange,
  className,
  wrapperClassName,
  showResetButton = true,
  showSubmitButton = true,
  submitButtonText,
  resetButtonText,
  footerClassName,
  layout = 'horizontal',
  columns = 3,
  collapsible = false,
  defaultCollapsed = false,
  title,
  subtitle,
}) => {
  const tCommon = useTranslations('common');
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [formValues, setFormValues] = useState<Record<string, any>>(() =>
    fields.reduce(
      (acc, field) => {
        if (field.defaultValue !== undefined && field.defaultValue !== '') {
          acc[field.name] = field.defaultValue;
        }
        return acc;
      },
      {} as Record<string, any>
    )
  );

  const sButtonText = submitButtonText || tCommon('applyFilters');
  const rButtonText = resetButtonText || tCommon('clearFilters');
  const [additionalData, setAdditionalData] = useState<Record<string, any>>({});

  const filterUiMap = fields.reduce(
    (acc, field) => {
      acc[field.name] = {
        label: field.label || field.name,
        type: field.type,
        options: field.options,
        keys: field.keys,
        multiple: field.multiple || field.type === 'multiselect',
      };
      return acc;
    },
    {} as Record<string, any>
  );

  const { filter } = useFilter(
    fields.map(field => field.name),
    filterUiMap
  );
  const prevFilterRef = useRef<Record<string, any>>({});

  const form = useForm({
    defaultValues: fields.reduce(
      (acc, field) => {
        acc[field.name] = field.defaultValue || '';
        return acc;
      },
      {} as Record<string, any>
    ),
  });

  // Clear hidden fields' values
  useEffect(() => {
    fields.forEach(field => {
      if (field.hidden) {
        form.setValue(field.name, '');
      }
    });
  }, [fields, form]);

  // useEffect(() => {
  //   // Update form values when fields change
  //   const newValues = fields.reduce(
  //     (acc, field) => {
  //       acc[field.name] = field.defaultValue || '';
  //       return acc;
  //     },
  //     {} as Record<string, any>
  //   );
  //   console.log('newValues111', newValues);
  //   setFormValues(newValues);
  //   form.reset(newValues);
  // }, [fields, form]);

  useEffect(() => {
    const filterString = JSON.stringify(filter);
    const prevFilterString = JSON.stringify(prevFilterRef.current);

    if (filterString !== prevFilterString) {
      setFormValues(filter);
      prevFilterRef.current = filter;
    }
  }, [filter]);

  const handleFieldChange = (fieldName: string, value: any, additionalData?: any) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
    onFieldChange?.(fieldName, value, additionalData);
  };

  const handleSubmit = () => {
    onSubmit?.(formValues, additionalData);
  };

  const handleReset = () => {
    const resetValues = fields.reduce(
      (acc, field) => {
        if (field.type === 'daterange') {
          acc[field.name] = { startDate: '', endDate: '' };
        } else if (field.type === 'range-slider') {
          acc[field.name] = [field.rangeMin ?? 0, field.rangeMax ?? 100];
        } else if (field.type === 'number-range' || field.type === 'range-select') {
          acc[field.name] = { min: '', max: '' };
        }
        if (field.multiple) {
          acc[field.name] = [];
        } else {
          acc[field.name] = field.defaultValue || '';
        }
        return acc;
      },
      {} as Record<string, any>
    );

    setFormValues(resetValues);
    form.reset(resetValues);
    onReset?.();
  };

  const renderField = (field: FilterField) => {
    const fieldValue = formValues[field.name] || field.defaultValue || '';

    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'time':
      case 'datetime-local':
      case 'search':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            <Input
              type={field.type}
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={e => handleFieldChange(field.name, e.target.value, field)}
              disabled={field.disabled}
              className={cn(FILTER_INPUT_CLASS_NAME, field.className)}
              wrapperClassName={cn(FILTER_INPUT_WRAPPER_CLASS_NAME)}
              label={field.label}
              labelClassName={FILTER_LABEL_CLASS_NAME}
              showAsterisk={field.required}
            />
          </div>
        );
      case 'tag-input':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <TagsInput
              key={field.name}
              initialTags={fieldValue || []}
              inputProps={{ placeholder: field.placeholder }}
              wrapperClassName="min-h-[38px]"
              onTagsChange={value => handleFieldChange(field.name, value, field)}
            />
          </div>
        );
      case 'date':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <DatePicker
              mode="range"
              triggerClassNames={FILTER_SELECT_CLASS_NAME}
              applied={fieldValue}
              onApply={date => handleFieldChange(field.name, date)}
              onClear={() => handleFieldChange(field.name, '')}
            />
          </div>
        );
      case 'select':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <CustomSelect
              key={`filter-${field.name}`}
              options={field.options || []}
              placeholder={field.placeholder}
              defaultValue={fieldValue}
              onOptionSelect={option => {
                const value = Array.isArray(option) ? option : option?.id;
                handleFieldChange(field.name, value, option);
              }}
              searchable={field.searchable}
              clearable={field.clearable}
              disabled={field.disabled}
              triggerClassNames={cn(FILTER_SELECT_CLASS_NAME, field.className)}
              onSearch={search => field.onSearch?.(search)}
              searchLoading={field.searchLoading}
              showScrollButtons
            />
          </div>
        );
      case 'user-select':
        return (
          <UserSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            searchable={field.searchable}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'contact-select':
        return (
          <ContactSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            searchable={field.searchable}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            filterParams={field.filterParams}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'company-select':
        return (
          <CompanySelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            searchable={field.searchable}
            clearable={field.clearable}
            disabled={field.disabled}
            multiple={field.multiple}
            defaultValue={fieldValue}
            placeholder={field.placeholder}
            varient={field.multiple ? 'inline-multiple' : 'default'}
            filterParams={field.filterParams}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option.map((o: any) => o.id) : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'campaign-select':
        return (
          <CampaignSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            searchable={field.searchable}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option.map((o: any) => o.id) : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'icp-select':
        return (
          <IcpSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            searchable={field.searchable}
            multiple={field.multiple}
            varient={field.multiple ? 'inline-multiple' : 'default'}
            placeholder={field.placeholder}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option.map((o: any) => o.id) : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'persona-select':
        return (
          <PersonaSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            searchable={field.searchable}
            placeholder={field.placeholder}
            multiple={field.multiple}
            varient={field.multiple ? 'inline-multiple' : 'default'}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option.map((o: any) => o.id) : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'target-list-select':
        return (
          <TargetlistSelect
            key={`filter-${field.name}`}
            label={field.label}
            required={field.required}
            wrapperClassName={field.wrapperClassName}
            fetchSelectedOptionOnMount
            className={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            clearable={field.clearable}
            disabled={field.disabled}
            defaultValue={fieldValue}
            searchable={field.searchable}
            placeholder={field.placeholder}
            multiple={field.multiple}
            varient={field.multiple ? 'inline-multiple' : 'default'}
            onOptionSelect={option => {
              const value = Array.isArray(option) ? option.map((o: any) => o.id) : option?.id;
              handleFieldChange(field.name, value, option);
              setAdditionalData(prev => ({ ...prev, [field.name]: option }));
            }}
          />
        );
      case 'multiselect':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <CustomSelect
              options={field.options || []}
              placeholder={field.placeholder}
              defaultValue={fieldValue}
              onOptionSelect={option => {
                const value = Array.isArray(option) ? option.map(item => item.id) : option?.id;
                handleFieldChange(field.name, value, option);
              }}
              multiple={true}
              varient="inline-multiple"
              searchable={field.searchable}
              clearable={field.clearable}
              disabled={field.disabled}
              triggerClassNames={cn(FILTER_SELECT_CLASS_NAME, field.className)}
            />
          </div>
        );

      case 'daterange':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <DatePicker
              key={`${field.name}-${fieldValue?.startDate || 'empty'}-${fieldValue?.endDate || 'empty'}`}
              mode="range"
              triggerClassNames={FILTER_SELECT_CLASS_NAME}
              showShortOptions={true}
              allowedShortOptions={['today', 'thisWeek', 'thisMonth']}
              disableFutureDates={
                field.disableFutureDates === undefined ? true : field.disableFutureDates
              }
              disablePastDates={field.disablePastDates}
              dateFormat="yyyy-MM-dd"
              applied={(() => {
                if (
                  fieldValue &&
                  typeof fieldValue === 'object' &&
                  fieldValue.startDate &&
                  fieldValue.endDate &&
                  fieldValue.startDate !== '' &&
                  fieldValue.endDate !== ''
                ) {
                  return {
                    from: new Date(fieldValue.startDate + 'T00:00:00'),
                    to: new Date(fieldValue.endDate + 'T23:59:59'),
                  };
                }
                return undefined;
              })()}
              onApply={dateRange => {
                if (
                  dateRange &&
                  typeof dateRange === 'object' &&
                  'from' in dateRange &&
                  'to' in dateRange
                ) {
                  const formatDateToYYYYMMDD = (date: Date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };

                  const formattedValue = {
                    startDate: dateRange.from ? formatDateToYYYYMMDD(dateRange.from) : '',
                    endDate: dateRange.to ? formatDateToYYYYMMDD(dateRange.to) : '',
                  };

                  handleFieldChange(field.name, formattedValue);
                }
              }}
              onClear={() => {
                handleFieldChange(field.name, { startDate: '', endDate: '' });
              }}
            />
          </div>
        );

      case 'range-slider': {
        const rangeValue: [number, number] = Array.isArray(fieldValue)
          ? (fieldValue as [number, number])
          : [field.rangeMin ?? 0, field.rangeMax ?? 100];
        return (
          <RangeSlider
            key={field.name}
            label={field.label}
            required={field.required}
            min={field.rangeMin ?? 0}
            max={field.rangeMax ?? 100}
            value={rangeValue}
            onValueChange={value => handleFieldChange(field.name, value)}
            className={field.wrapperClassName}
          />
        );
      }

      case 'range-select': {
        const rangeValue =
          fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)
            ? (fieldValue as { min?: string | number; max?: string | number })
            : { min: '', max: '' };
        const selectedOption = field.options?.find(option => {
          const optionRange = option.meta?.range as
            | { min?: string | number; max?: string | number }
            | undefined;
          if (!optionRange) return false;
          return (
            String(optionRange.min || '') === String(rangeValue.min || '') &&
            String(optionRange.max || '') === String(rangeValue.max || '')
          );
        });

        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <CustomSelect
              key={`filter-${field.name}`}
              options={field.options || []}
              placeholder={field.placeholder}
              defaultValue={selectedOption?.id}
              onOptionSelect={option => {
                const selected = Array.isArray(option) ? option[0] : option;
                const optionRange = selected?.meta?.range as
                  | { min?: string | number; max?: string | number }
                  | undefined;
                handleFieldChange(
                  field.name,
                  optionRange
                    ? {
                        min: optionRange.min ? String(optionRange.min) : '',
                        max: optionRange.max ? String(optionRange.max) : '',
                      }
                    : { min: '', max: '' },
                  selected
                );
              }}
              searchable={field.searchable}
              clearable={field.clearable}
              disabled={field.disabled}
              triggerClassNames={cn(FILTER_SELECT_CLASS_NAME, field.className)}
              showScrollButtons
            />
          </div>
        );
      }

      case 'switch':
        return (
          <div
            key={field.name}
            className={cn('flex h-[38px] items-center', field.wrapperClassName)}
          >
            <Switch
              checked={fieldValue === 'true' || fieldValue === true}
              onCheckedChange={checked =>
                handleFieldChange(field.name, checked ? 'true' : '', field)
              }
              disabled={field.disabled}
            >
              <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
            </Switch>
          </div>
        );

      case 'divider':
        return (
          <div
            key={field.name}
            className={cn('border-t border-border md:col-span-2', field.wrapperClassName)}
          />
        );

      case 'number-range': {
        const rangeValue =
          fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)
            ? (fieldValue as { min?: string; max?: string })
            : { min: '', max: '' };
        const sliderMin = field.rangeMin ?? 0;
        const sliderMax = field.rangeMax ?? 100;
        const sliderValue: [number, number] = [
          rangeValue.min ? Number(rangeValue.min) : sliderMin,
          rangeValue.max ? Number(rangeValue.max) : sliderMax,
        ];
        const normalizeRangeInput = (value: string) => {
          let nextValue = value;
          if (field.validation?.maxLength && nextValue.length > field.validation.maxLength) {
            nextValue = nextValue.slice(0, field.validation.maxLength);
          }
          if (field.validation?.max !== undefined && Number(nextValue) > field.validation.max) {
            nextValue = String(field.validation.max);
          }
          if (field.validation?.min !== undefined && Number(nextValue) < field.validation.min) {
            nextValue = String(field.validation.min);
          }
          return nextValue;
        };

        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            {field.label && (
              <label className={FILTER_LABEL_CLASS_NAME}>
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder={field.minPlaceholder}
                value={rangeValue.min || ''}
                onChange={event =>
                  handleFieldChange(
                    field.name,
                    {
                      min: normalizeRangeInput(event.target.value),
                      max: rangeValue.max || '',
                    },
                    field
                  )
                }
                disabled={field.disabled}
                maxLength={field.validation?.maxLength}
                min={field.validation?.min}
                max={field.validation?.max}
                className={cn(FILTER_INPUT_CLASS_NAME, field.className)}
                wrapperClassName={cn(FILTER_INPUT_WRAPPER_CLASS_NAME)}
              />
              <Input
                type="number"
                placeholder={field.maxPlaceholder}
                value={rangeValue.max || ''}
                onChange={event =>
                  handleFieldChange(
                    field.name,
                    {
                      min: rangeValue.min || '',
                      max: normalizeRangeInput(event.target.value),
                    },
                    field
                  )
                }
                disabled={field.disabled}
                maxLength={field.validation?.maxLength}
                min={field.validation?.min}
                max={field.validation?.max}
                className={cn(FILTER_INPUT_CLASS_NAME, field.className)}
                wrapperClassName={cn(FILTER_INPUT_WRAPPER_CLASS_NAME)}
              />
            </div>
            {field.showRangeSlider && (
              <div className="mt-3">
                <RangeSlider
                  min={sliderMin}
                  max={sliderMax}
                  step={field.rangeStep ?? 1}
                  value={sliderValue}
                  showValues={false}
                  onValueChange={value =>
                    handleFieldChange(field.name, {
                      min: String(value[0]),
                      max: String(value[1]),
                    })
                  }
                />
                <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{field.rangeMinLabel ?? sliderMin}</span>
                  <span>{field.rangeMaxLabel ?? sliderMax}</span>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'website-url-rules':
        return (
          <WebsiteUrlRulesInput
            key={field.name}
            value={Array.isArray(fieldValue) ? (fieldValue as WebsiteUrlRule[]) : []}
            onChange={value => handleFieldChange(field.name, value, field)}
            maxRules={field.maxRules}
            layout="compact"
            showHeader={true}
            includeTargetPageMetadata={field.includeTargetPageMetadata}
            isActive={field.isActive}
            labels={{
              title: field.label,
              description: field.description,
              enterUrl: field.websiteUrlRuleLabels?.enterUrl || field.placeholder || '',
              visitsPlaceholder: field.websiteUrlRuleLabels?.visitsPlaceholder || '',
              visitCount: field.websiteUrlRuleLabels?.visitCount || '',
              targetPages: field.websiteUrlRuleLabels?.targetPages || '',
              totalTimeSpent: field.websiteUrlRuleLabels?.totalTimeSpent || '',
              timeSpentPlaceholder: field.websiteUrlRuleLabels?.timeSpentPlaceholder || '',
              exactMatch: field.websiteUrlRuleLabels?.exactMatch || '',
              matchTypes: field.websiteUrlRuleLabels?.matchTypes,
              visits: field.websiteUrlRuleLabels?.visits || '',
              time: field.websiteUrlRuleLabels?.time || '',
              clearTime: field.websiteUrlRuleLabels?.clearTime || '',
              minuteSuffix: field.websiteUrlRuleLabels?.minuteSuffix || '',
              secondSuffix: field.websiteUrlRuleLabels?.secondSuffix || '',
              add: field.websiteUrlRuleLabels?.add || '',
              remove: field.websiteUrlRuleLabels?.remove || '',
              operators: field.websiteUrlRuleLabels?.operators || {
                greater_than: '',
                greater_than_or_equal: '',
                less_than: '',
                less_than_or_equal: '',
              },
            }}
            className={field.wrapperClassName}
          />
        );

      case 'checkbox':
        return (
          <div key={field.name} className={cn('flex flex-col', field.wrapperClassName)}>
            <Checkbox
              checked={fieldValue === 'true' || fieldValue === true}
              onCheckedChange={checked => handleFieldChange(field.name, checked)}
              disabled={field.disabled}
            >
              {field.label}
            </Checkbox>
          </div>
        );

      default:
        return null;
    }
  };

  const getLayoutClasses = () => {
    switch (layout) {
      case 'vertical':
        return 'flex flex-col space-y-4 pb-4';
      case 'grid':
        return cn(
          'grid grid-cols-1 gap-4',
          columns === 1 && 'md:grid-cols-1',
          columns === 2 && 'md:grid-cols-2',
          columns === 3 && 'md:grid-cols-2 lg:grid-cols-3',
          columns > 3 && 'md:grid-cols-2 lg:grid-cols-4'
        );
      case 'horizontal':
      default:
        return 'flex flex-wrap items-end gap-4';
    }
  };

  const renderVisibleFields = () => {
    const visibleFields = fields.filter(f => !f.hidden);
    const renderedFields: React.ReactNode[] = [];

    for (let index = 0; index < visibleFields.length; index += 1) {
      const field = visibleFields[index];

      if (field.type === 'switch') {
        const switchFields: FilterField[] = [];
        while (visibleFields[index]?.type === 'switch') {
          switchFields.push(visibleFields[index]);
          index += 1;
        }
        index -= 1;

        renderedFields.push(
          <div
            key={switchFields.map(switchField => switchField.name).join('-')}
            className="flex flex-wrap items-center gap-x-4 gap-y-3 md:col-span-2"
          >
            {switchFields.map(renderField)}
          </div>
        );
        continue;
      }

      renderedFields.push(renderField(field));
    }

    return renderedFields;
  };

  const filterContent = (
    <div className={cn('flex flex-col flex-1 overflow-hidden', className)}>
      {title && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {collapsible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2"
            >
              <Icon
                name={isCollapsed ? 'arrow-down-s-fill' : 'arrow-up'}
                size={16}
                className="text-muted-foreground"
              />
            </Button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        <CustomScroll orientation="vertical" viewportClassName="pr-0">
          <div className={cn(getLayoutClasses(), wrapperClassName)}>{renderVisibleFields()}</div>
        </CustomScroll>
      </div>

      {(showSubmitButton || showResetButton) && (
        <div
          className={cn(
            'flex items-center gap-3 pt-5 border-t border-border justify-between px-4',
            footerClassName
          )}
        >
          {showResetButton && (
            <Button
              variant="link"
              onClick={() => {
                handleReset();
              }}
              className=" text-error-base px-0"
            >
              {rButtonText}
            </Button>
          )}
          {showSubmitButton && (
            <Button
              variant="default"
              onClick={form.handleSubmit(handleSubmit)}
              className="min-w-[120px]"
            >
              {sButtonText}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  if (collapsible) {
    return (
      <div className="bg-background border border-border rounded-lg p-4">
        {title && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2"
            >
              <Icon
                name={isCollapsed ? 'arrow-down-s-fill' : 'arrow-up'}
                size={16}
                className="text-muted-foreground"
              />
            </Button>
          </div>
        )}

        {!isCollapsed && filterContent}
      </div>
    );
  }

  return filterContent;
};

export default Filter;
